const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const CLOSE_DELAY = 110;
const PREVIEW_DELAY = 45;
const PREVIEW_FADE_DURATION = 60;

class CatexPremiumMenu extends HTMLElement {
  #items = [];
  #activeItem = null;
  #closeTimer = null;
  #lastPointerType = 'mouse';
  #abortController = null;
  #previewTimer = null;
  #previewFadeTimer = null;
  #previewRequestId = 0;

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#items = Array.from(this.querySelectorAll('[data-catex-menu-item]'));
    this.#decorateSpecialItems();
    this.#placeCustomPromos();

    this.#items.forEach((item, index) => {
      const trigger = item.querySelector('[data-catex-menu-trigger]');
      const panel = item.querySelector('[data-catex-menu-panel]');
      if (!(trigger instanceof HTMLAnchorElement)) return;

      item.addEventListener(
        'pointerenter',
        () => {
          if (!window.matchMedia(HOVER_QUERY).matches) return;
          if (panel) this.#open(item);
          else this.#close();
        },
        { signal }
      );

      item.addEventListener(
        'pointerleave',
        () => {
          if (panel && window.matchMedia(HOVER_QUERY).matches) this.#scheduleClose(item);
        },
        { signal }
      );

      trigger.addEventListener(
        'focus',
        () => {
          if (panel) this.#open(item);
          else this.#close();
        },
        { signal }
      );

      trigger.addEventListener(
        'pointerdown',
        (event) => {
          this.#lastPointerType = event.pointerType || 'mouse';
        },
        { signal }
      );

      trigger.addEventListener(
        'click',
        (event) => {
          if (!panel || this.#lastPointerType !== 'touch') return;
          if (this.#activeItem !== item) {
            event.preventDefault();
            this.#open(item);
          }
        },
        { signal }
      );

      trigger.addEventListener(
        'keydown',
        (event) => this.#handleTriggerKeydown(event, item, index),
        { signal }
      );

      if (panel instanceof HTMLElement) this.#bindPanelPreviews(panel, signal);
    });

    this.addEventListener(
      'focusout',
      (event) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && this.contains(nextTarget)) return;
        this.#scheduleClose(this.#activeItem, 0);
      },
      { signal }
    );

    this.querySelector('[data-catex-menu-scrim]')?.addEventListener('click', () => this.#close(), { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.#activeItem) return;
        const trigger = this.#activeItem.querySelector('[data-catex-menu-trigger]');
        this.#close();
        if (trigger instanceof HTMLElement) trigger.focus();
      },
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.#close();
      },
      { signal }
    );

    window.addEventListener('resize', () => this.#close(), { signal, passive: true });

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!(event.target instanceof Element) || !this.contains(event.target)) return;
        const promo = event.target.closest('[data-catex-custom-promo]');
        const panel = promo?.closest('[data-catex-menu-panel]');
        const item = panel?.closest('[data-catex-menu-item]');
        if (item instanceof HTMLElement) this.#open(item);
      },
      { signal }
    );

    this.dataset.catexReady = 'true';
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearCloseTimer();
    this.#clearPreviewTimer();
    this.#previewRequestId += 1;
    this.#activeItem = null;
    delete this.dataset.catexReady;
  }

  #decorateSpecialItems() {
    this.#items.forEach((item) => {
      const trigger = item.querySelector('[data-catex-menu-trigger]');
      if (!(trigger instanceof HTMLAnchorElement)) return;

      const label = Array.from(trigger.children).find(
        (child) => child instanceof HTMLSpanElement && !child.classList.contains('catex-menu__badge')
      );
      const normalizedTitle = (label?.textContent || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

      let kind = '';
      if (normalizedTitle === 'lancamentos' || normalizedTitle === 'lancamento') {
        kind = 'launches';
      } else if (normalizedTitle === 'promocoes' || normalizedTitle === 'promocao') {
        kind = 'promotions';
      }

      if (!kind) return;

      item.classList.add(`catex-menu__item--${kind}`);
      item.dataset.catexSpecialMenu = kind;
    });
  }

  #bindPanelPreviews(panel, signal) {
    const previewTriggers = Array.from(panel.querySelectorAll('[data-catex-preview-trigger]'));

    previewTriggers.forEach((previewTrigger) => {
      previewTrigger.addEventListener(
        'pointerenter',
        () => {
          if (!window.matchMedia(HOVER_QUERY).matches || !window.matchMedia('(min-width: 990px)').matches) return;
          this.#schedulePanelPreview(panel, previewTrigger);
        },
        { signal }
      );

      previewTrigger.addEventListener(
        'focus',
        () => {
          if (!window.matchMedia('(min-width: 990px)').matches) return;
          this.#schedulePanelPreview(panel, previewTrigger, 0);
        },
        { signal }
      );
    });

    panel.addEventListener(
      'pointerleave',
      () => {
        this.#clearPreviewTimer();
        this.#previewRequestId += 1;
        this.#resetPanelPreview(panel);
      },
      { signal }
    );
  }

  #schedulePanelPreview(panel, previewTrigger, delay = PREVIEW_DELAY) {
    this.#clearPreviewTimer();
    const requestId = ++this.#previewRequestId;

    this.#previewTimer = window.setTimeout(() => {
      this.#previewTimer = null;
      this.#loadPanelPreview(panel, previewTrigger, requestId);
    }, delay);
  }

  #loadPanelPreview(panel, previewTrigger, requestId) {
    const src = previewTrigger.getAttribute('data-catex-preview-src');
    if (requestId !== this.#previewRequestId) return;
    if (!src) {
      this.#resetPanelPreview(panel);
      return;
    }
    if (panel.dataset.catexPreviewCurrent === src) return;

    const image = panel.querySelector('[data-catex-preview-image]');
    const card = panel.querySelector('[data-catex-preview-card]');
    if (!(image instanceof HTMLImageElement) || !(card instanceof HTMLAnchorElement)) return;

    const previewData = {
      src,
      srcset: previewTrigger.getAttribute('data-catex-preview-srcset') || '',
      alt: previewTrigger.getAttribute('data-catex-preview-alt') || '',
      caption: previewTrigger.getAttribute('data-catex-preview-label') || '',
    };
    const loader = new Image();
    let settled = false;

    const revealPreview = () => {
      if (settled) return;
      settled = true;
      if (requestId !== this.#previewRequestId) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.#applyPanelPreview(panel, previewData);
        return;
      }

      card.classList.add('is-switching');
      this.#previewFadeTimer = window.setTimeout(() => {
        this.#previewFadeTimer = null;
        if (requestId !== this.#previewRequestId) {
          card.classList.remove('is-switching');
          return;
        }

        this.#applyPanelPreview(panel, previewData);
        requestAnimationFrame(() => card.classList.remove('is-switching'));
      }, PREVIEW_FADE_DURATION);
    };

    loader.addEventListener('load', revealPreview, { once: true });
    loader.addEventListener('error', () => {
      settled = true;
    }, { once: true });
    loader.decoding = 'async';
    loader.sizes = image.sizes || '160px';
    if (previewData.srcset) loader.srcset = previewData.srcset;
    loader.src = previewData.src;

    if (loader.complete && loader.naturalWidth > 0) queueMicrotask(revealPreview);
  }

  #applyPanelPreview(panel, previewData) {
    const image = panel.querySelector('[data-catex-preview-image]');
    const caption = panel.querySelector('[data-catex-preview-caption]');
    if (!(image instanceof HTMLImageElement)) return;

    if (previewData.srcset) image.srcset = previewData.srcset;
    else image.removeAttribute('srcset');
    image.src = previewData.src;
    image.alt = previewData.alt;
    if (caption instanceof HTMLElement && previewData.caption) caption.textContent = previewData.caption;
    panel.dataset.catexPreviewCurrent = previewData.src;
  }

  #resetPanelPreview(panel) {
    if (!(panel instanceof HTMLElement) || !panel.dataset.catexPreviewCurrent) return;

    const image = panel.querySelector('[data-catex-preview-image]');
    const card = panel.querySelector('[data-catex-preview-card]');
    const caption = panel.querySelector('[data-catex-preview-caption]');
    if (!(image instanceof HTMLImageElement) || !(card instanceof HTMLAnchorElement)) return;

    const primarySrc = image.getAttribute('data-catex-primary-src');
    if (!primarySrc || panel.dataset.catexPreviewCurrent === primarySrc) return;

    this.#applyPanelPreview(panel, {
      src: primarySrc,
      srcset: image.getAttribute('data-catex-primary-srcset') || '',
      alt: image.getAttribute('data-catex-primary-alt') || '',
      caption: caption?.getAttribute('data-catex-primary-caption') || '',
    });
    card.classList.remove('is-switching');
  }

  #clearPreviewTimer() {
    if (this.#previewTimer !== null) window.clearTimeout(this.#previewTimer);
    if (this.#previewFadeTimer !== null) window.clearTimeout(this.#previewFadeTimer);
    this.#previewTimer = null;
    this.#previewFadeTimer = null;
    this.querySelectorAll('.catex-menu__promo-card.is-switching').forEach((card) => {
      card.classList.remove('is-switching');
    });
  }

  #placeCustomPromos() {
    const bank = this.querySelector('[data-catex-promo-bank]');
    if (!bank) return;

    if (this.classList.contains('catex-menu--compact')) {
      bank.remove();
      return;
    }

    const panels = Array.from(this.querySelectorAll('[data-catex-menu-panel]'));
    const promos = Array.from(bank.querySelectorAll('[data-catex-custom-promo]'));

    promos.forEach((promo) => {
      const target = promo.getAttribute('data-catex-menu-target') || '';
      const panel =
        panels.find((candidate) => candidate.getAttribute('data-catex-menu-panel') === target) || panels[0];
      const tray = panel?.querySelector('[data-catex-promo-tray]');
      if (!tray) return;

      tray.querySelectorAll('[data-catex-auto-promo]').forEach((automaticPromo) => automaticPromo.remove());
      tray.append(promo);
    });

    bank.remove();
  }

  #open(item) {
    const panel = item?.querySelector('[data-catex-menu-panel]');
    const trigger = item?.querySelector('[data-catex-menu-trigger]');
    if (!(item instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    this.#clearCloseTimer();
    this.#clearPreviewTimer();
    this.#previewRequestId += 1;

    if (this.#activeItem && this.#activeItem !== item) {
      this.#resetPanelPreview(this.#activeItem.querySelector('[data-catex-menu-panel]'));
      this.#setItemState(this.#activeItem, false);
    }

    const isCompact = this.classList.contains('catex-menu--compact');
    if (isCompact) this.#fitCompactPanel(panel, trigger);

    this.#activeItem = item;
    this.#setItemState(item, true);
    this.dataset.menuOpen = 'true';

    if (isCompact) {
      const visibleImageLimit = window.matchMedia('(min-width: 990px)').matches ? 1 : 0;
      this.#activateImages(panel, visibleImageLimit);
    } else {
      this.#activateImages(panel);
    }
  }

  #close(item = this.#activeItem) {
    this.#clearCloseTimer();
    this.#clearPreviewTimer();
    this.#previewRequestId += 1;
    if (!(item instanceof HTMLElement)) return;

    this.#resetPanelPreview(item.querySelector('[data-catex-menu-panel]'));
    this.#setItemState(item, false);
    if (item === this.#activeItem) this.#activeItem = null;
    if (!this.#activeItem) delete this.dataset.menuOpen;
  }

  #setItemState(item, open) {
    const trigger = item.querySelector('[data-catex-menu-trigger]');
    const panel = item.querySelector('[data-catex-menu-panel]');

    if (open) item.dataset.open = 'true';
    else delete item.dataset.open;

    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel?.setAttribute('aria-hidden', open ? 'false' : 'true');
  }

  #scheduleClose(item, delay = CLOSE_DELAY) {
    this.#clearCloseTimer();
    this.#closeTimer = window.setTimeout(() => this.#close(item), delay);
  }

  #clearCloseTimer() {
    if (this.#closeTimer === null) return;
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  #activateImages(panel, limit = Infinity) {
    Array.from(panel.querySelectorAll('[data-catex-lazy-image]')).slice(0, limit).forEach((image) => {
      if (!(image instanceof HTMLImageElement) || image.src) return;
      const src = image.dataset.src;
      const srcset = image.dataset.srcset;
      if (srcset) image.srcset = srcset;
      if (src) image.src = src;
      delete image.dataset.src;
      delete image.dataset.srcset;
    });
  }

  #fitCompactPanel(panel, trigger) {
    const viewportMargin = 16;
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const panelWidth = Math.min(panel.getBoundingClientRect().width, viewportWidth - viewportMargin * 2);
    const triggerRect = trigger.getBoundingClientRect();
    const item = panel.closest('[data-catex-menu-item]');
    const navigation = panel.closest('.catex-menu__nav');

    if (!(item instanceof HTMLElement) || !(navigation instanceof HTMLElement)) return;

    const navigationRect = navigation.getBoundingClientRect();
    let idealLeft = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;

    if (item.classList.contains('catex-menu__item--edge-left')) idealLeft = triggerRect.left;
    if (item.classList.contains('catex-menu__item--edge-right')) idealLeft = triggerRect.right - panelWidth;

    const maximumLeft = Math.max(viewportMargin, viewportWidth - viewportMargin - panelWidth);
    const fittedLeft = Math.min(Math.max(idealLeft, viewportMargin), maximumLeft);

    panel.style.left = `${Math.round(fittedLeft - navigationRect.left)}px`;
    panel.style.right = 'auto';
    panel.dataset.catexPositioned = 'true';
  }

  #handleTriggerKeydown(event, item, index) {
    const triggers = this.#items
      .map((menuItem) => menuItem.querySelector('[data-catex-menu-trigger]'))
      .filter((trigger) => trigger instanceof HTMLElement);

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (index + direction + triggers.length) % triggers.length;
      triggers[nextIndex]?.focus();
      return;
    }

    if (event.key === 'ArrowDown') {
      const panel = item.querySelector('[data-catex-menu-panel]');
      if (!panel) return;
      event.preventDefault();
      this.#open(item);
      requestAnimationFrame(() => panel.querySelector('a')?.focus());
    }
  }
}

if (!customElements.get('catex-premium-menu')) {
  customElements.define('catex-premium-menu', CatexPremiumMenu);
}
